import { BreakpointObserver } from '@angular/cdk/layout';
import { ConnectedPosition, Overlay, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ViewportRuler } from '@angular/cdk/scrolling';
import {
  DestroyRef,
  Directive,
  effect,
  EffectRef,
  ElementRef,
  inject,
  input,
  InputSignal,
  OnInit,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  TemplateRef,
  ViewContainerRef,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ThemeBase } from '@libs/themes';
import { filter, firstValueFrom } from 'rxjs';
import { overlayBoundarySelector } from './overlay-boundary.directive';

@Directive({
  selector: '[uiOverlay]',
  host: { '(click)': 'show()' },
})
export class OverlayDirective implements OnInit {
  private readonly overlay: Overlay = inject(Overlay);
  private readonly viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
  private readonly elementRef: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly breakpointObserver: BreakpointObserver = inject(BreakpointObserver);
  private readonly viewportRuler: ViewportRuler = inject(ViewportRuler);

  private readonly overlayRef: WritableSignal<OverlayRef | undefined> = signal(undefined);
  private readonly breakpointMd: string = `(min-width: ${ThemeBase.Breakpoints.md}px)`;
  private readonly backdropClass: string = 'cdk-overlay-transparent-backdrop';
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly viewportChange: Signal<unknown> = toSignal(this.viewportRuler.change(0), { initialValue: undefined });

  private readonly updatePositionEffectRef: EffectRef = effect(() => {
    const ref: OverlayRef | undefined = this.overlayRef();

    // triggers effect when viewport changes, so we can update the position strategy and size of the overlay
    this.viewportChange();

    /* istanbul ignore else */
    if (ref?.hasAttached()) {
      ref.updatePositionStrategy(this.getPositionStrategy());
      ref.updateSize({
        width: this.getOverlayWidth(),
      });
    }
  });

  public content: InputSignal<TemplateRef<unknown>> = input.required<TemplateRef<unknown>>();
  public classList: InputSignal<string[]> = input<string[]>([]);
  public positionStrategy: InputSignal<PositionStrategy | undefined> = input<PositionStrategy | undefined>();
  public positionStrategyMobile: InputSignal<PositionStrategy | undefined> = input<PositionStrategy | undefined>();
  public matchElementWidth: InputSignal<boolean> = input<boolean>(false);
  public hasBackdrop: InputSignal<boolean> = input<boolean>(true);
  public backdropScrollable: InputSignal<boolean> = input<boolean>(false);
  public flexibleConnectedPositionDesktop: InputSignal<ConnectedPosition> = input<ConnectedPosition>({
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
  });

  public flexibleConnectedPositionMobile: InputSignal<ConnectedPosition> = input<ConnectedPosition>({
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
  });

  public overlayDisabled: InputSignal<boolean> = input<boolean>(false);

  public overlayClosed: OutputEmitterRef<void> = output();

  public overlayShown: OutputEmitterRef<void> = output();

  public ngOnInit(): void {
    this.overlayRef.set(this.overlay.create({
      hasBackdrop: this.hasBackdrop(),
      panelClass: [...this.classList(), 'overlay-panel'],
      backdropClass: this.backdropClass,
      positionStrategy: this.getPositionStrategy(),
      width: this.getOverlayWidth(),
      scrollStrategy: this.hasBackdrop() ? this.overlay.scrollStrategies.block() : this.overlay.scrollStrategies.noop(),
    }));

    this.destroyRef.onDestroy(() => {
      this.overlayRef()?.dispose();
    });
  }

  public show(): void {
    const ref: OverlayRef | undefined = this.overlayRef();

    if (!ref || ref.hasAttached() || this.overlayDisabled()) {
      return;
    }

    const portal: TemplatePortal<unknown> = new TemplatePortal(this.content(), this.viewContainerRef, { overlay: this });

    ref.attach(portal);
    ref.updateSize({
      width: this.getOverlayWidth(),
    });

    this.overlayShown.emit();

    void firstValueFrom(
      ref.outsidePointerEvents().pipe(
        filter((event: MouseEvent) => !this.shouldIgnoreClose(event)),
      ),
    ).then(() => void this.close());
  }

  public close(): void {
    const ref: OverlayRef | undefined = this.overlayRef();

    if (ref?.hasAttached()) {
      ref.detach();
      this.overlayClosed.emit();
    }
  }

  private isMobile(): boolean {
    return !this.breakpointObserver.isMatched(this.breakpointMd);
  }

  private getPositionStrategy(): PositionStrategy {
    return this.isMobile()
      ? this.positionStrategyMobile() ?? this.getDefaultMobilePositionStrategy()
      : this.positionStrategy() ?? this.getDefaultPositionStrategy();
  }

  private getDefaultPositionStrategy(): PositionStrategy {
    return this.overlay.position()
      .flexibleConnectedTo(this.elementRef)
      .withFlexibleDimensions(true)
      .withPositions([
        this.flexibleConnectedPositionDesktop(),
      ]);
  }

  private getDefaultMobilePositionStrategy(): PositionStrategy {
    return this.overlay.position()
      .flexibleConnectedTo(this.elementRef)
      .withFlexibleDimensions(true)
      .withPositions([
        this.flexibleConnectedPositionMobile(),
      ]);
  }

  private getOverlayWidth(): number | string | undefined {
    if (this.matchElementWidth()) {
      return this.elementRef.nativeElement.getBoundingClientRect().width;
    }

    return undefined;
  }

  private shouldIgnoreClose(event: MouseEvent): boolean {
    const target: Element | null = event.target as Element | null;

    if (!target) {
      return false;
    }

    return this.elementRef.nativeElement.contains(target)
      || this.isInsideBoundary(target)
      || this.isStackedAbove(target);
  }

  private isInsideBoundary(target: Element): boolean {
    return this.elementRef.nativeElement.closest(overlayBoundarySelector)?.contains(target) ?? false;
  }

  private isStackedAbove(target: Element): boolean {
    const ownHost: HTMLElement | undefined = this.overlayRef()?.hostElement;
    const stacked: Element | undefined = Array.from(ownHost?.parentElement?.children ?? [])
      .find(sibling => sibling.contains(target));

    if (!ownHost || !stacked) {
      return false;
    }

    return (ownHost.compareDocumentPosition(stacked) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
  }
}
